/**
 * Customer Detail Component
 * Container for individual customer view with all their data
 */

import React from 'react';
import { User, Mail, Calendar, Activity, Clock } from 'lucide-react';
import { CustomerDetailResponse, TimeRange } from '../types';
import { ActivityLog } from './ActivityLog';
import { CustomerAssets } from './CustomerAssets';
import { UseCaseCostBreakdown } from '../../freeTier/components/UseCaseCostBreakdown';
import { CostChart } from '../../freeTier/components/CostChart';
import { UseCaseRunsChart } from '../../freeTier/components/UseCaseRunsChart';
import { CostByModelChart } from '../../freeTier/components/CostByModelChart';

interface CustomerDetailProps {
  data: CustomerDetailResponse;
  timeRange: TimeRange;
}

const BRAND_PRIMARY = '#03e8d3';

export const CustomerDetail: React.FC<CustomerDetailProps> = ({ data, timeRange }) => {
  const { customer, activity_log, audiences, simulations } = data;

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Customer Info Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold"
              style={{ backgroundColor: BRAND_PRIMARY }}
            >
              {(customer.name || customer.email)[0].toUpperCase()}
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {customer.name || 'Unnamed Customer'}
              </h2>
              <div className="flex items-center gap-2 text-gray-500 mt-1">
                <Mail className="w-4 h-4" />
                <span>{customer.email}</span>
              </div>
              <div className="flex items-center gap-4 mt-3">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  {customer.subscription_status}
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                  {customer.subscription_plan}
                </span>
              </div>
            </div>
          </div>

          <div className="text-right">
            <div className="text-sm text-gray-500">Customer ID</div>
            <div className="text-lg font-mono text-gray-900">#{customer.id}</div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-gray-100">
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 text-gray-500 text-sm mb-1">
              <Calendar className="w-4 h-4" />
              Member Since
            </div>
            <div className="text-lg font-semibold text-gray-900">
              {formatDate(customer.created_at)}
            </div>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 text-gray-500 text-sm mb-1">
              <Clock className="w-4 h-4" />
              Subscription Start
            </div>
            <div className="text-lg font-semibold text-gray-900">
              {formatDate(customer.subscription_start_date)}
            </div>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 text-gray-500 text-sm mb-1">
              <Activity className="w-4 h-4" />
              Monthly Simulations
            </div>
            <div className="text-lg font-semibold text-gray-900">
              {customer.monthly_simulation_count.toLocaleString()}
            </div>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 text-gray-500 text-sm mb-1">
              <User className="w-4 h-4" />
              Audience Requests
            </div>
            <div className="text-lg font-semibold text-gray-900">
              {customer.total_audience_requests.toLocaleString()}
            </div>
          </div>
        </div>
      </div>

      {/* Row 1: Use Case Cost Breakdown | Cost Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <UseCaseCostBreakdown data={data.usecase_cost_breakdown} />
        <CostChart
          costData={data.cost_data}
          granularity={data.granularity}
        />
      </div>

      {/* Row 2: Use Case Runs | Cost by Model */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <UseCaseRunsChart data={data.usecase_run_counts} costData={data.usecase_cost_breakdown} />
        <CostByModelChart data={data.cost_data.cost_by_model} />
      </div>

      {/* Activity Log */}
      <ActivityLog activities={activity_log} />

      {/* Customer Assets */}
      <CustomerAssets audiences={audiences} simulations={simulations} />
    </div>
  );
};

export default CustomerDetail;
